import React from 'react';
import { SxStyle } from '../../../../../types';
import Box from '@mui/material/Box';
import { useStringsContext } from '../../../../../contexts/strings';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import {
  DataGrid,
  GridColDef,
  GridColumnHeaderClassNamePropType,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import CreateSquadOverlay from './create-squad-list-overlay';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { SquadPlayer } from '../../types';
import IconButton from '@mui/material/IconButton';
import { Typography } from '@mui/material';
import { translateCountry } from '../../../../../countries';
import PlayerAvatar from '../../../../common/player-avatar';

const styles: SxStyle = {
  dataGridContainer: {
    flexGrow: 1,
    width: '100%',
    marginTop: 4,
  },
  dataGridHeader: {
    '& > .MuiDataGrid-columnSeparator--sideRight': {
      visibility: 'hidden',
    },
  },
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
  textEllipse: {
    textOverflow: 'ellipsis', // TODO check double ellipse
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  link: {
    cursor: 'pointer',
  },
  healthDescription: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
};

interface Props {
  teamId: string;
  selectedSquad: SquadPlayer[] | null;
  loading: boolean;
  onCreateSquad: () => void;
  onEditPlayer: (player: SquadPlayer) => void;
  onStatsPlayer: (player: SquadPlayer) => void;
  onDeletePlayer: (player: SquadPlayer) => void;
  isReadOnly?: boolean;
}

interface ListAvatarProps {
  params: GridRenderCellParams;
  onClick: (player: SquadPlayer) => void;
}

interface EditButtonsProps
  extends Pick<Props, 'onEditPlayer' | 'onStatsPlayer' | 'onDeletePlayer'> {
  params: GridRenderCellParams;
  isReadOnly?: boolean;
}

interface ListPositionProps {
  params: GridRenderCellParams;
}

interface ListStatusProps {
  params: GridRenderCellParams;
}

interface ShirtNumberProps {
  params: GridRenderCellParams;
}

interface IdNumberProps {
  params: GridRenderCellParams;
  type: 'smcId' | 'fogisId' | 'optaId';
}

interface ListNationalityProps {
  params: GridRenderCellParams;
}

const ListAvatar = ({ params, onClick }: ListAvatarProps) => {
  const player: SquadPlayer = params.row;

  return (
    <Stack
      onClick={() => onClick(player)}
      sx={styles.link}
      spacing={4}
      direction="row"
      alignItems="center"
    >
      <PlayerAvatar
        srcImage={player.images?.sefImagePng}
        fullName={player.displayName || player.fullName}
      />
      <Typography variant="body2">{player.displayName || player.fullName}</Typography>
    </Stack>
  );
};

const ListEditButtons = ({
  params,
  onEditPlayer,
  onStatsPlayer,
  onDeletePlayer,
  isReadOnly,
}: EditButtonsProps) => (
  <Stack spacing={1} direction="row" alignItems="center">
    <IconButton onClick={() => onEditPlayer(params.row)}>
      <InfoIcon color="primary" />
    </IconButton>
    <IconButton sx={{ display: 'none' }} onClick={() => onStatsPlayer(params.row)}>
      <AnalyticsIcon color="primary" />
    </IconButton>
    <IconButton onClick={() => onDeletePlayer(params.row)} disabled={isReadOnly}>
      <DeleteIcon color={isReadOnly ? 'disabled' : 'error'} />
    </IconButton>
  </Stack>
);

const ListPosition = ({ params }: ListPositionProps) => {
  const { strings } = useStringsContext();
  const player: SquadPlayer = params.row;
  let positions = player.position?.primary ? [player.position?.primary] : [];
  positions = player.position?.other
    ? [...positions, ...(player.position?.other || {})]
    : positions;
  const value = positions.map((position) => strings.squadPlayerPositions[position]).join(' / ');

  return params.row.position.primary ? (
    <Box sx={styles.textEllipse}>{value}</Box>
  ) : (
    <Typography variant="italic">{strings.squad.missingInformation}</Typography>
  );
};

const ListStatus = ({ params }: ListStatusProps) => {
  const { strings } = useStringsContext();
  const player: SquadPlayer = params.row;
  const currentClub = player.activeCurrentClub;

  return currentClub?.status ? <>{strings.squadPlayer[currentClub.status]}</> : <></>;
};

const ShirtNumber = ({ params }: ShirtNumberProps) => {
  const player: SquadPlayer = params.row;

  return <>{player.activeCurrentClub?.shirtNumber || ''}</>;
};

const IdNumber = ({ params, type }: IdNumberProps) => {
  const player: SquadPlayer = params.row;

  return <>{player[type] || ''}</>;
};

const ListNationality = ({ params }: ListNationalityProps) => {
  const { strings } = useStringsContext();
  const player: SquadPlayer = params.row;

  return player.nationality ? (
    <>{translateCountry(player.nationality)}</>
  ) : (
    <Typography variant="italic">{strings.squad.missingInformation}</Typography>
  );
};

const SquadList = ({
  teamId,
  selectedSquad,
  loading,
  onCreateSquad,
  onEditPlayer,
  onStatsPlayer,
  onDeletePlayer,
  isReadOnly,
}: Props) => {
  const { strings } = useStringsContext();

  const defaultColumnOptions = {
    headerClassName: styles.dataGridHeader as GridColumnHeaderClassNamePropType, // TODO Check
    maxWidth: 300,
  };

  const columns: GridColDef[] = [
    {
      field: 'fullName',
      headerName: strings.squad.player,
      ...defaultColumnOptions,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <ListAvatar onClick={onEditPlayer} params={params} />
      ),
    },
    {
      field: 'number',
      headerName: strings.squadPlayer.shirtNumber,
      ...defaultColumnOptions,
      renderCell: (params: GridRenderCellParams) => <ShirtNumber params={params} />,
      valueGetter: (value, row) => row.activeCurrentClub.shirtNumber,
    },
    {
      field: 'position',
      headerName: strings.squadPlayer.position,
      ...defaultColumnOptions,
      minWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <ListPosition params={params} />,
      valueGetter: (value, row) => row.position.primary,
    },
    {
      field: 'nationality',
      headerName: strings.squadPlayer.nationality,
      ...defaultColumnOptions,
      minWidth: 150,
      maxWidth: 200,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <ListNationality params={params} />,
    },
    /*
    Disabled (https://linear.app/sef/issue/SMA-493)
    { field: 'active', headerName: strings.squadPlayer.active, ...defaultColumnOptions, minWidth: 100, maxWidth: '100px', flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <PlayerActiveSelect
          teamId={teamId}
          player={params.row}
          onUpdatePlayer={() => {}}/>
      ),
      valueGetter: (params) => params.row.activeCurrentClub.active,
    },
    */
    {
      field: 'status',
      headerName: strings.squad.status,
      ...defaultColumnOptions,
      minWidth: 100,
      maxWidth: 150,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <ListStatus params={params} />,
      valueGetter: (value, row) => row.activeCurrentClub.status,
    },
    {
      field: 'smcId',
      headerName: strings.squadPlayer.smcId,
      ...defaultColumnOptions,
      minWidth: 100,
      maxWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <IdNumber params={params} type={'smcId'} />,
    },
    {
      field: 'fogisId',
      headerName: strings.squadPlayer.fogisId,
      ...defaultColumnOptions,
      minWidth: 100,
      maxWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <IdNumber params={params} type={'fogisId'} />,
    },
    {
      field: 'optaId',
      headerName: strings.squadPlayer.optaId,
      ...defaultColumnOptions,
      minWidth: 100,
      maxWidth: 100,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <IdNumber params={params} type={'optaId'} />,
    },
    {
      field: 'clId',
      headerName: '',
      headerClassName: styles.dataGridHeader as GridColumnHeaderClassNamePropType, // TODO Check
      minWidth: 200,
      flex: 1,
      sortable: false,
      align: 'right',
      renderCell: (params: GridRenderCellParams) => (
        <ListEditButtons
          params={params}
          onEditPlayer={onEditPlayer}
          onStatsPlayer={onStatsPlayer}
          onDeletePlayer={onDeletePlayer}
          isReadOnly={isReadOnly}
        />
      ),
    },
  ];

  return (
    <Paper elevation={2} sx={styles.dataGridContainer}>
      <DataGrid
        sx={styles.dataGrid}
        rows={selectedSquad || []}
        columns={columns}
        getRowId={(row) => row.clId}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        initialState={{
          sorting: {
            sortModel: [
              {
                field: 'fullName',
                sort: 'asc',
              },
            ],
          },
        }}
        slots={{
          noRowsOverlay: () => (
            <CreateSquadOverlay
              onClick={() => onCreateSquad()}
              isSquadEmpty={!!(selectedSquad && selectedSquad.length > 0)}
              loading={loading}
              disabled={!selectedSquad}
            />
          ),
        }}
      />
    </Paper>
  );
};

export default SquadList;
